import React, {
  useEffect, createContext, useCallback, useRef, useState,
  FunctionComponent,
  Dispatch,
  SetStateAction,
} from 'react';
import api from '../api';
import { CcsAccountType } from '../enums';
import { useNotification } from '../hooks';

export interface AppStateProviderProps {
  accountId: string;
  themeMode: 'dark' | 'light';
  children: React.ReactNode;
  isAuthed: boolean;
  setTheme: (arg: 'dark' | 'light') => Promise<void>;
  setAccountId: Dispatch<SetStateAction<string>>;
}

export interface AppStateProviderValues {
  isLoading: boolean;
  isCcsV2: boolean;
  accountId: string;
  themeMode: 'dark' | 'light';
  refresh: () => void;
  features: { [key: string]: boolean } | null;
  setTheme: (arg: 'dark' | 'light') => Promise<void>;
  logout: () => void;
  setAccountId: (accountId: string) => void;
  ccsAccountType: CcsAccountType | null;
}

const appState = createContext<AppStateProviderValues>({
  isLoading: true,
  isCcsV2: false,
  accountId: '',
  themeMode: 'dark',
  refresh: () => {},
  features: null,
  setTheme: async (arg = 'dark') => {},
  logout: () => {},
  setAccountId: () => {},
  ccsAccountType: null,
});
const { Provider } = appState;

const AppStateProvider: FunctionComponent<
AppStateProviderProps
> = ({
  accountId, themeMode, children, isAuthed, setTheme, setAccountId,
}: AppStateProviderProps) => {
  const isMounted = useRef<boolean>(false);
  const [isLoadingSwitches, setLoadingSwitches] = useState<boolean>(true);
  const [isLoadingCredentials, setLoadingCredentials] = useState<boolean>(false);
  const [hasVaultCredentials, setHasVaultCredentials] = useState<boolean>(false);
  const [features, setFeatures] = useState<{ [key: string]: boolean } | null>(null);
  const [isFlaggedV2, setFlaggedV2] = useState<boolean>(false);
  const [ccsAccountType, setCcsAccountType] = useState<CcsAccountType | null>(null);
  const [isLoadingAccountType, setLoadingAccountType] = useState<boolean>(true);
  const [initialAppLoaded, setInitialAppLoaded] = useState<boolean>(false);
  const { addNotification } = useNotification();

  const logout = useCallback(
    async () => {
      try {
        localStorage.removeItem('accountId');
        await api.auth.logout(accountId);
      } catch (error) {
        addNotification(error, 'error');
      }

      // refresh the page
      window.history.pushState({}, '', '/');
      window.location.reload();
    },
    [accountId, addNotification],
  );

  const updateFeatures = useCallback(async () => {
    if (isAuthed && accountId) {
      if (isMounted.current) {
        setLoadingSwitches(true);
      }

      try {
        const featuresResult = await api.mavenFeatures.getFeatures(accountId);
        if (isMounted.current) {
          setFeatures((prev: any) => ({
            ...prev,
            ...featuresResult,
          }));

          if (featuresResult['maven.feature.isMigratedCcsV2']) {
            setFlaggedV2(true);
          } else {
            setFlaggedV2(false);
            setInitialAppLoaded(true);
          }
        }

        if (featuresResult['maven.feature.themes']) {
          const themeResult = await api.account.getTheme(accountId);
          if (isMounted.current) {
            setTheme(themeResult.theme);
          }
        }
      } catch (err) {
        addNotification(err, 'error');
      } finally {
        if (isMounted.current) {
          setLoadingSwitches(false);
        }
      }
    }
  }, [accountId, addNotification, isAuthed, setTheme]);

  const verifyHasVaultCredentials = useCallback(async () => {
    if (accountId && isAuthed) {
      let verifiedVaultCredentials = false;

      if (isMounted.current) {
        setLoadingCredentials(true);
      }
      try {
        const resp = await api.ccsV2.isActive(accountId);
        verifiedVaultCredentials = !!resp;
        if (isMounted.current) {
          setHasVaultCredentials(verifiedVaultCredentials);
        }
      } catch (err) {
        if (isMounted.current) {
          setHasVaultCredentials(false);
        }
      } finally {
        if (isMounted.current) {
          setLoadingCredentials(false);
          setInitialAppLoaded(true);
        }
      }
    }
  }, [accountId, isAuthed]);

  const doSetAccountId = useCallback(async (accId: string) => {
    setAccountId(accId);
  }, [setAccountId]);

  const initAccountProperties = useCallback(
    () => {
      updateFeatures();
    }, [updateFeatures],
  );

  const refresh = useCallback(() => {
    setLoadingAccountType(true);
    initAccountProperties();
  }, [initAccountProperties]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthed && accountId) {
      setLoadingAccountType(true);
    }
  }, [accountId, isAuthed]);

  useEffect(() => {
    if (!initialAppLoaded) {
      initAccountProperties();
    }
  }, [initialAppLoaded, initAccountProperties]);

  useEffect(() => {
    if (!isLoadingSwitches) {
      if (isFlaggedV2) {
        verifyHasVaultCredentials();
      } else {
        setLoadingCredentials(false);
        setHasVaultCredentials(false);
      }
    }
  }, [isFlaggedV2, verifyHasVaultCredentials, isLoadingSwitches]);

  useEffect(() => {
    if (accountId && isAuthed && hasVaultCredentials && isFlaggedV2) {
      setFeatures((prev: any) => ({
        ...prev,
        isCcsV2: true,
      }));
    } else {
      setFeatures((prev: any) => ({
        ...prev,
        isCcsV2: false,
      }));
    }
  }, [accountId, hasVaultCredentials, isAuthed, isFlaggedV2]);

  useEffect(() => {
    if (initialAppLoaded) {
      if (!features) {
        setCcsAccountType(CcsAccountType.INVALID);
      } else if (isFlaggedV2) {
        if (hasVaultCredentials) {
          setCcsAccountType(CcsAccountType.V2);
        } else {
          setCcsAccountType(CcsAccountType.INVALID);
        }
      } else {
        setCcsAccountType(CcsAccountType.V1);
      }
      setLoadingAccountType(false);
    } else {
      setLoadingAccountType(true);
    }
  }, [isFlaggedV2, hasVaultCredentials, features, initialAppLoaded]);

  useEffect(() => {
    const createMisToken = async () => await api.ccsV2.createMisToken(accountId);

    if (ccsAccountType === CcsAccountType.V2) {
      // populate MIS token in vault for ccs-app accounts only
      createMisToken();
    }
  }, [ccsAccountType, accountId]);

  return (
    <Provider value={{
      accountId,
      isLoading: isLoadingCredentials || isLoadingSwitches || isLoadingAccountType,
      isCcsV2: ccsAccountType === CcsAccountType.V2,
      themeMode,
      refresh,
      features,
      setTheme,
      logout,
      setAccountId: doSetAccountId,
      ccsAccountType,
    }}
    >
      {children}
    </Provider>
  );
};

export {
  appState,
};
export default AppStateProvider;
